import React from 'react';
import { Copy, ExternalLink, Globe, CreditCard, Server, Users } from 'lucide-react';
import { useSettingsStore } from '../store/settingsStore';
import { TeamManagement } from '../components/Settings/TeamManagement';

export const AppSettings = () => {
  const { razorpay, updateRazorpaySettings } = useSettingsStore();
  const [copied, setCopied] = React.useState('');
  const [razorpayKeys] = React.useState({
    keyId: 'rzp_test_vmJG00z8Ft0Oto',
    keySecret: 'egsiIm9iAbikw0Tc9eT15q2Y'
  });
  const [saved, setSaved] = React.useState(false);

  // Auto-save on component mount
  React.useEffect(() => {
    updateRazorpaySettings(razorpayKeys);
  }, []);

  const nameservers = [
    'ns1.checkout-builder.com',
    'ns2.checkout-builder.com'
  ];

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Settings</h1>
      
      <div className="space-y-6">
        {/* Team Management */}
        <TeamManagement />

        {/* Domain Settings */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Domain Settings</h2>
                <p className="text-sm text-gray-500">Configure your custom domain for checkout pages</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Domain
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="yourdomain.com"
                    className="input-field"
                  />
                  <button className="btn">
                    Add Domain
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nameservers
                </label>
                <div className="space-y-2">
                  {nameservers.map((ns, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={ns}
                        readOnly
                        className="flex-1 input-field bg-gray-50"
                      />
                      <button
                        onClick={() => handleCopy(ns, `ns${index}`)}
                        className="btn btn-secondary p-2"
                      >
                        {copied === `ns${index}` ? (
                          <span className="text-green-600">Copied!</span>
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Update these nameservers in your domain registrar's settings.
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Server className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium">Domain Status</span>
              </div>
              <span className="px-3 py-1 bg-yellow-50 text-yellow-700 text-sm font-medium rounded-full">
                Pending
              </span>
            </div>
          </div>
        </div>

        {/* Razorpay Settings */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Razorpay Integration</h2>
                <p className="text-sm text-gray-500">Configure your Razorpay payment gateway</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key ID
                </label>
                <input
                  type="text"
                  value={razorpayKeys.keyId}
                  readOnly
                  className="input-field bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key Secret
                </label>
                <input
                  type="password"
                  value={razorpayKeys.keySecret}
                  readOnly
                  className="input-field bg-gray-50"
                />
              </div>

              <div className="pt-4">
                <a 
                  href="https://dashboard.razorpay.com/app/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  View keys in Razorpay Dashboard
                </a>
              </div>

              <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                <Server className="w-5 h-5" />
                <span className="text-sm font-medium">Razorpay integration is active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};